import React, { useContext, useEffect, useState } from "react";
import { Modal, Form, Input, TimePicker, Switch, message, theme } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ScheduleTemplate } from "../../../../../types/scheduleTemplate";
import { ScheduleTempOperation } from "../../../../../serviceoperation/ScheduleTempOperation";
import { CompanyContext } from "../../../../../contexts/company-context";

interface ScheduleTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (template: Partial<ScheduleTemplate>) => void;
  template?: ScheduleTemplate | null;
  mode: "create" | "edit";
}

const ScheduleTemplateModal: React.FC<ScheduleTemplateModalProps> = ({
  open,
  onClose,
  onSave,
  template,
  mode,
}) => {
  const [form] = Form.useForm();
  const { token } = theme.useToken();
  const { selectedCompany } = useContext(CompanyContext);
  const [loading, setLoading] = useState<boolean>(false);
  const tempOps = new ScheduleTempOperation();

  useEffect(() => {
    if (open) {
      if (mode === "edit" && template) {
        form.setFieldsValue({
          name: template.name,
          description: template.description,
          startTime: dayjs()
            .hour(template.startWorkHour)
            .minute(template.startWorkMin),
          endTime: dayjs()
            .hour(template.endWorkHour)
            .minute(template.endWorkMin),
          isDefault: template.isDefault,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          isDefault: false,
        });
      }
    }
  }, [open, mode, template, form]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const templateData: Partial<ScheduleTemplate> = {
        name: values.name,
        description: values.description,
        startWorkHour: values.startTime.hour(),
        startWorkMin: values.startTime.minute(),
        endWorkHour: values.endTime.hour(),
        endWorkMin: values.endTime.minute(),
        isDefault: values.isDefault || false,
      };

      if (mode === "edit" && template) {
        templateData.id = template.id;
        tempOps
          .putScheduleTempAsync(
            selectedCompany?.corporationId as number,
            Number(template.id),
            {
              name: values.name,
              startHour: values.startTime.hour(),
              startMinute: values.startTime.minute(),
              endHour: values.endTime.hour(),
              endMinute: values.endTime.minute(),
            },
          )
          .then((value) => {
            if (value.statusCode === 200) {
              onSave(templateData);
              onClose();
              form.resetFields();
              message.success(`Schedule template updated successfully!`);
            } else {
              message.error(
                "An error has occured while processing your request!",
              );
            }
            setLoading(false);
          });
      } else {
        tempOps
          .postScheduleTempAsync(selectedCompany?.corporationId as number, {
            name: values.name,
            startHour: values.startTime.hour(),
            startMinute: values.startTime.minute(),
            endHour: values.endTime.hour(),
            endMinute: values.endTime.minute(),
          })
          .then((value) => {
            if (value.statusCode === 201) {
              onSave(templateData);
              onClose();
              form.resetFields();
              message.success(
                `Schedule template ${
                  mode === "create" ? "created" : "updated"
                } successfully!`,
              );
            } else {
              message.error(
                "An error has occured while processing your request!",
              );
            }
            setLoading(false);
          });
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ClockCircleOutlined style={{ color: token.colorPrimary }} />
          {mode === "create"
            ? "Create Schedule Template"
            : "Edit Schedule Template"}
        </div>
      }
      open={open}
      confirmLoading={loading}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={mode === "create" ? "Create Template" : "Update Template"}
      width={600}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
        <Form.Item
          name="name"
          label="Template Name"
          rules={[
            { required: true, message: "Please enter template name" },
            { min: 3, message: "Template name must be at least 3 characters" },
          ]}
        >
          <Input placeholder="e.g., Standard Business Hours" />
        </Form.Item>

        {/*<Form.Item
          name="description"
          label="Description"
          rules={[
            { required: true, message: "Please enter template description" },
          ]}
        >
          <Input.TextArea
            rows={3}
            placeholder="Describe when this template should be used"
          />
        </Form.Item>*/}

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
        >
          <Form.Item
            name="startTime"
            label="Start Time"
            rules={[{ required: true, message: "Please select start time" }]}
          >
            <TimePicker
              format="h:mm A"
              style={{ width: "100%" }}
              placeholder="Select start time"
            />
          </Form.Item>

          <Form.Item
            name="endTime"
            label="End Time"
            rules={[
              { required: true, message: "Please select end time" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || !getFieldValue("startTime")) {
                    return Promise.resolve();
                  }

                  const startTime = getFieldValue("startTime");
                  const startMinutes =
                    startTime.hour() * 60 + startTime.minute();
                  const endMinutes = value.hour() * 60 + value.minute();

                  // Handle overnight shifts
                  if (endMinutes <= startMinutes && endMinutes < 12 * 60) {
                    // This is likely an overnight shift (e.g., 10 PM to 6 AM)
                    return Promise.resolve();
                  }

                  if (endMinutes <= startMinutes) {
                    return Promise.reject(
                      new Error("End time must be after start time"),
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <TimePicker
              format="h:mm A"
              style={{ width: "100%" }}
              placeholder="Select end time"
            />
          </Form.Item>
        </div>

        {/*<Form.Item
          name="isDefault"
          valuePropName="checked"
          style={{ marginBottom: 0 }}
        >
          <Switch />
          <span style={{ marginLeft: 8 }}>Set as default template</span>
        </Form.Item>*/}
      </Form>
    </Modal>
  );
};

export default ScheduleTemplateModal;
